import type { ReactElement } from 'react'
import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  BeakerIcon,
  ScaleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import { cn } from 'app/lib/utils'
import type { ModelAnalysis } from './analysis'
import { MIN_CHANGED, MIN_ITEMS, formatAccuracy, formatPoints, type Verdict } from './stats'

type Tone = { panel: string; icon: string; Icon: typeof ScaleIcon }

const TONE: Record<Verdict, Tone> = {
  improved: { panel: `border-green-200 bg-green-50`, icon: `text-green-700`, Icon: ArrowTrendingUpIcon },
  regressed: { panel: `border-red-200 bg-red-50`, icon: `text-red-700`, Icon: ArrowTrendingDownIcon },
  equivalent: { panel: `border-default bg-surface-raised`, icon: `text-subtle`, Icon: ScaleIcon },
  identical: { panel: `border-default bg-surface-raised`, icon: `text-subtle`, Icon: ScaleIcon },
  inconclusive: { panel: `border-amber-200 bg-amber-50`, icon: `text-amber-700`, Icon: QuestionMarkCircleIcon },
  'too-few': { panel: `border-amber-200 bg-amber-50`, icon: `text-amber-700`, Icon: QuestionMarkCircleIcon },
}

/** The run's verdict in plain words; "significant" and p-values deliberately never appear. */
export const VERDICT_LABEL: Record<Verdict, string> = {
  improved: `Better`,
  regressed: `Worse`,
  equivalent: `No meaningful difference`,
  identical: `Identical`,
  inconclusive: `Can't tell yet`,
  'too-few': `Can't tell yet`,
}

type Copy = { title: string; body: string }

function describe(
  analysis: ModelAnalysis,
  candidate: string,
  baseline: string,
  sameSides: boolean,
  marginSource: string | null,
): Copy {
  const { comparison: c, totalFields } = analysis
  const n = c.items
  const fmt = (value: number) => formatPoints(value, n)
  // Unsigned, with its unit: "1 point", "4 points".
  const unsigned = (value: number) => {
    const text = fmt(Math.abs(value)).replace(/^[+±]/, ``)
    return text === `1` ? `1 point` : `${text} points`
  }
  const scores = `${candidate} scored ${formatAccuracy(analysis.candidate.matched / (analysis.candidate.total || 1), n)} and ${baseline} ${formatAccuracy(analysis.baseline.matched / (analysis.baseline.total || 1), n)} on ${n} calls (${totalFields} fields).`
  const range = c.interval ? `${fmt(c.interval.low)} to ${fmt(c.interval.high)} points` : ``

  if (sameSides) {
    return c.verdict === `identical`
      ? { title: `Noise check: no noise`, body: `${scores} Every call scored the same both times.` }
      : {
          title: `Noise check`,
          body: `${scores} The same prompt moved ${fmt(c.delta)} points (95% range ${range || `n/a`}) between two runs. Differences this size in comparisons on this dataset are noise.`,
        }
  }

  switch (c.verdict) {
    case `identical`:
      return { title: `Identical results`, body: `${scores} Every call scored the same under both prompts.` }
    case `too-few`:
      return n < MIN_ITEMS
        ? {
            title: `Can't tell yet: too few calls`,
            body: `${scores} With under ${MIN_ITEMS} calls one field moves the score by about ${unsigned(1 / (totalFields || 1))}, so no difference can be told from chance. Add at least ${MIN_ITEMS - n} more calls.`,
          }
        : {
            title: `Can't tell yet: too few calls changed`,
            body: `${scores} Only ${c.improved + c.regressed} ${c.improved + c.regressed === 1 ? `call` : `calls`} changed (${c.improved} better, ${c.regressed} worse). Claiming a direction needs at least ${MIN_CHANGED} changed calls; add calls that exercise what the prompt changed.`,
          }
    case `equivalent`:
      return {
        title: `No meaningful difference`,
        body: `${scores} The 95% range for the difference, ${range}, sits inside ±${unsigned(c.margin)}${marginSource ? `, the noise measured by ${marginSource}` : `, the smallest difference worth acting on`}.`,
      }
    case `inconclusive`:
      return {
        title: `Can't tell yet`,
        body: `${scores} The 95% range for the difference, ${range}, includes both a real gain and a real loss. This run can detect changes of about ${unsigned(c.detectable ?? 0)}; smaller effects need more calls.`,
      }
    default: {
      const better = c.verdict === `improved`
      const fewDrive = better ? c.regressed >= c.improved : c.improved >= c.regressed
      return {
        title: `${candidate} is ${better ? `better than` : `worse than`} ${baseline}`,
        body: `${scores} The difference is ${fmt(c.delta)} points, likely between ${range}.${fewDrive ? ` As many calls moved the other way; check them before trusting this.` : ``}`,
      }
    }
  }
}

/** The difference, its 95% range and the no-meaningful-difference band, centered on zero. */
function IntervalChart({ analysis }: { analysis: ModelAnalysis }): ReactElement | null {
  const { comparison: c } = analysis
  if (!c.interval || c.verdict === `identical`) return null
  const { low, high } = c.interval
  const extent = Math.max(Math.abs(low), Math.abs(high), c.margin, 0.02) * 1.2
  const at = (value: number) => ((value + extent) / (2 * extent)) * 100
  const tone =
    c.verdict === `improved` ? `bg-green-600` : c.verdict === `regressed` ? `bg-red-600` : `bg-[var(--fg-subtle)]`

  return (
    <figure className="w-full shrink-0 sm:w-72" aria-label="Difference with its 95% range">
      <div className="relative h-9">
        <div
          className="absolute inset-y-1 rounded bg-[var(--line-subtle)]"
          style={{ left: `${at(-c.margin)}%`, width: `${at(c.margin) - at(-c.margin)}%` }}
          title="No meaningful difference"
        />
        <div className="absolute inset-x-0 top-1/2 h-px bg-[var(--line-default)]" />
        <div className="absolute inset-y-0 w-px bg-[var(--line-strong)]" style={{ left: `${at(0)}%` }} />
        <div
          className={cn(`absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full`, tone)}
          style={{ left: `${at(low)}%`, width: `${Math.max(at(high) - at(low), 0.5)}%` }}
        />
        <div
          className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[var(--surface)] shadow-elevation-1"
          style={{ left: `${at(c.delta)}%`, backgroundColor: `var(--fg-strong)` }}
        />
      </div>
      <figcaption className="mt-0.5 flex justify-between text-caption-sm tabular-nums text-minimal">
        <span>{formatPoints(low, c.items)}</span>
        <span className="text-subtle">{formatPoints(c.delta, c.items)} pts · 95% range</span>
        <span>{formatPoints(high, c.items)}</span>
      </figcaption>
    </figure>
  )
}

export function VerdictPanel({
  analysis,
  candidateLabel,
  baselineLabel,
  sameSides,
  simulated,
  marginSource,
  allChangesUnstable,
}: {
  analysis: ModelAnalysis
  candidateLabel: string
  baselineLabel: string
  /** Candidate and baseline ran the same prompts (an A/A noise check). */
  sameSides: boolean
  simulated: boolean
  /** What set the no-meaningful-difference margin, e.g. "the noise check on Sep 23". */
  marginSource: string | null
  /** Every changed call also changed in the noise check: the movement is likely noise. */
  allChangesUnstable: boolean
}): ReactElement {
  const { comparison, fields } = analysis
  const tone = TONE[comparison.verdict]
  const copy = describe(analysis, candidateLabel, baselineLabel, sameSides, marginSource)
  const fixed = fields.reduce((sum, field) => sum + field.fixed, 0)
  const broke = fields.reduce((sum, field) => sum + field.broke, 0)
  const Icon = sameSides ? BeakerIcon : tone.Icon

  return (
    <section
      className={cn(`rounded-xl border p-4 sm:p-5`, sameSides ? `border-default bg-surface-raised` : tone.panel)}
      aria-label="Verdict"
    >
      {simulated && (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-caption-sm text-amber-800">
          Simulated scores: made-up errors, not a real model. This is not evidence about either prompt.
        </p>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 gap-3">
          <Icon className={cn(`mt-0.5 h-6 w-6 shrink-0`, sameSides ? `text-subtle` : tone.icon)} />
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-strong">{copy.title}</h3>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-default">{copy.body}</p>
            {allChangesUnstable && (
              <p className="mt-2 max-w-2xl text-sm font-medium text-amber-800">
                Every call that changed also changed in {marginSource ?? `the latest noise check`} with identical
                prompts, so this movement is likely noise, not the prompt change.
              </p>
            )}
          </div>
        </div>
        <IntervalChart analysis={analysis} />
      </div>
      <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-[var(--line-default)] pt-3 text-sm">
        <div className="flex items-baseline gap-1.5">
          <dt className="text-subtle">Calls</dt>
          <dd className="tabular-nums">
            <span className="text-green-700">{comparison.improved} better</span>
            <span className="text-minimal"> · </span>
            <span className="text-red-700">{comparison.regressed} worse</span>
            <span className="text-minimal"> · </span>
            <span className="text-subtle">{comparison.tied} same</span>
          </dd>
        </div>
        <div className="flex items-baseline gap-1.5">
          <dt className="text-subtle">Fields</dt>
          <dd className="tabular-nums">
            <span className="text-green-700">{fixed} fixed</span>
            <span className="text-minimal"> · </span>
            <span className="text-red-700">{broke} broke</span>
          </dd>
        </div>
      </dl>
    </section>
  )
}
